import { useEffect, useState } from "react";
import { api, formatBRL, formatPerWeight, WEIGHT_UNITS } from "@/lib/api";
import { ChartBar, Info, ArrowUpRight, ArrowDown, Equals, LinkSimple } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { useSettings } from "@/context/SettingsContext";
import UnitToggle from "@/components/UnitToggle";

export default function Comparativo() {
  const { unit } = useSettings();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/comparativo").then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, []);

  const matched = rows.filter((r) => r.tracked).length;
  const unitLabel = WEIGHT_UNITS.find((u) => u.value === unit)?.label || "100g";

  return (
    <div className="space-y-6" data-testid="comparativo-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="label-eyebrow mb-2">Comparativo</div>
          <h1 className="h-display" style={{ color: "var(--text-primary)" }}>Preço Base × Mercado Online</h1>
          <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--text-secondary)" }}>
            Cruzamento automático (fuzzy match) entre o seu portfólio e os produtos rastreados. Padronizado por peso.
          </p>
        </div>
        <UnitToggle />
      </header>

      {matched === 0 && rows.length > 0 && (
        <div className="flex items-start gap-2 text-xs px-3 py-2" style={{ color: "var(--warning)", background: "rgba(180,83,9,0.06)", border: "1px solid rgba(180,83,9,0.25)" }}>
          <Info size={14} weight="duotone" className="mt-0.5" />
          <span>
            Nenhuma correspondência automática encontrada. Use <Link to="/rastrear" className="gold-text underline">Rastrear Produto</Link> e busque termos parecidos com o portfólio (ex.: "pimenta do reino", "tempero churrasco").
          </span>
        </div>
      )}

      <section className="surface">
        <div className="px-6 py-4 border-b flex items-center gap-2 flex-wrap" style={{ borderColor: "var(--border-default)" }}>
          <ChartBar size={18} weight="duotone" style={{ color: "var(--gold)" }} />
          <h2 className="text-lg font-medium" style={{ color: "var(--text-primary)" }}>Tabela comparativa</h2>
          <span className="ml-auto text-xs" style={{ color: "var(--text-secondary)" }}>
            {matched}/{rows.length} vínculos automáticos
          </span>
        </div>
        {loading ? (
          <div className="p-8 text-sm" style={{ color: "var(--text-secondary)" }}>Carregando…</div>
        ) : (
          <div className="table-scroll">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left" style={{ color: "var(--text-secondary)" }}>
                  <th className="px-6 py-3 label-eyebrow">Produto base</th>
                  <th className="px-6 py-3 label-eyebrow">Categoria</th>
                  <th className="px-6 py-3 label-eyebrow">Vínculo automático</th>
                  <th className="px-6 py-3 label-eyebrow">Base /{unitLabel}</th>
                  <th className="px-6 py-3 label-eyebrow">Médio mercado</th>
                  <th className="px-6 py-3 label-eyebrow text-right">Δ vs base</th>
                  <th className="px-6 py-3 label-eyebrow">Alerta</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const p = r.portfolio;
                  const diff = r.diff_pct;
                  return (
                    <tr key={p.id} className="border-t surface-hover" style={{ borderColor: "var(--border-default)" }} data-testid={`compare-row-${p.id}`}>
                      <td className="px-6 py-3" style={{ color: "var(--text-primary)" }}>{p.name}</td>
                      <td className="px-6 py-3" style={{ color: "var(--text-secondary)" }}>{p.category}</td>
                      <td className="px-6 py-3">
                        {r.tracked ? (
                          <Link to={`/rastrear/${r.tracked.id}`} className="text-xs inline-flex items-center gap-1" style={{ color: "var(--gold)" }}>
                            <LinkSimple size={11} /> {r.candidate_query}
                            <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>· {(r.match_score * 100).toFixed(0)}%</span>
                          </Link>
                        ) : (
                          <span className="text-xs" style={{ color: "var(--text-muted)" }}>sem vínculo</span>
                        )}
                      </td>
                      <td className="px-6 py-3" style={{ color: r.base_norm ? "var(--text-primary)" : "var(--text-muted)" }}>
                        {r.base_norm ? formatPerWeight(r.base_norm, unit) : "n/d"}
                      </td>
                      <td className="px-6 py-3" style={{ color: "var(--gold)" }}>
                        {r.tracked && r.tracked.avg_norm ? formatPerWeight(r.tracked.avg_norm, unit) : "—"}
                      </td>
                      <td className="px-6 py-3 text-right">
                        {diff == null ? (
                          <span className="text-xs" style={{ color: "var(--text-muted)" }}>—</span>
                        ) : (
                          <span className="text-xs font-medium" style={{ color: diff > 5 ? "var(--success)" : diff < -5 ? "var(--danger)" : "var(--gold)" }}>
                            {diff > 0 ? "+" : ""}{diff.toFixed(0)}%
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-3">
                        {r.alert ? <AlertBadge level={r.alert.level} text={r.alert.text} /> : (
                          <span className="text-xs" style={{ color: "var(--text-muted)" }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <p className="text-xs" style={{ color: "var(--text-muted)" }}>
        Para alertas precisos, inclua peso no nome do item base (ex.: "Tempero 200g") e mantenha rastreamentos com termos próximos do portfólio.
      </p>
    </div>
  );
}

const AlertBadge = ({ level, text }) => {
  const cfg = {
    opportunity: { color: "var(--success)", bg: "rgba(21,128,61,0.08)", border: "rgba(21,128,61,0.25)", icon: ArrowUpRight, label: "Oportunidade" },
    risk: { color: "var(--danger)", bg: "rgba(220,38,38,0.08)", border: "rgba(220,38,38,0.25)", icon: ArrowDown, label: "Risco" },
    ok: { color: "var(--gold)", bg: "rgba(184,148,31,0.08)", border: "rgba(184,148,31,0.25)", icon: Equals, label: "Paridade" },
  }[level] || { color: "var(--text-muted)", bg: "transparent", border: "transparent", icon: Equals, label: "—" };
  const Icon = cfg.icon;
  return (
    <span
      className="text-[11px] inline-flex items-center gap-1 px-2 py-1"
      style={{ color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}` }}
      title={text}
    >
      <Icon size={11} weight="bold" /> {cfg.label}
    </span>
  );
};
